/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * 
 * Local Asset Database
 * 
 * IndexedDB wrapper for storing asset metadata and thumbnails locally.
 * HD assets are stored on Google Drive; this stores lightweight metadata + previews.
 */

const DB_NAME = 'studio_jenial_assets';
const DB_VERSION = 1;
const STORE_NAME = 'assets';

export interface LocalAssetRecord {
    id: string;
    type: 'image' | 'video';
    driveFileId: string;
    thumbnailBase64: string; // Compressed preview (256px, ~50-100KB)
    filename: string;
    prompt?: string;
    modelId?: string;
    createdAt: string;
    size: number; // Original file size in bytes
    projectId?: string;
}

let dbInstance: IDBDatabase | null = null;

/**
 * Get or create the IndexedDB database
 */
async function getDB(): Promise<IDBDatabase> {
    if (dbInstance) return dbInstance;

    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;

            if (!db.objectStoreNames.contains(STORE_NAME)) {
                const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
                store.createIndex('type', 'type', { unique: false });
                store.createIndex('createdAt', 'createdAt', { unique: false });
                store.createIndex('projectId', 'projectId', { unique: false });
            }
        };

        request.onsuccess = (event) => {
            dbInstance = (event.target as IDBOpenDBRequest).result;
            resolve(dbInstance);
        };

        request.onerror = () => {
            reject(new Error('Failed to open IndexedDB'));
        };
    });
}

/**
 * Save an asset record
 */
export async function saveAsset(record: LocalAssetRecord): Promise<void> {
    const db = await getDB();

    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const request = store.put(record);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(new Error('Failed to save asset'));
    });
}

/**
 * Get all assets, optionally filtered by type
 */
export async function getAssets(type?: 'image' | 'video'): Promise<LocalAssetRecord[]> {
    const db = await getDB();

    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);

        let request: IDBRequest;

        if (type) {
            const index = store.index('type');
            request = index.getAll(type);
        } else {
            request = store.getAll();
        }

        request.onsuccess = () => {
            const results = request.result as LocalAssetRecord[];
            // Sort by createdAt descending (newest first)
            results.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
            resolve(results);
        };
        request.onerror = () => reject(new Error('Failed to get assets'));
    });
}

/**
 * Get a single asset by ID
 */
export async function getAssetById(id: string): Promise<LocalAssetRecord | null> {
    const db = await getDB();

    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const request = store.get(id);

        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(new Error('Failed to get asset'));
    });
}

/**
 * Delete an asset by ID
 */
export async function deleteAsset(id: string): Promise<void> {
    const db = await getDB();

    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const request = store.delete(id);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(new Error('Failed to delete asset'));
    });
}

/**
 * Get assets for a specific project
 */
export async function getAssetsByProject(projectId: string): Promise<LocalAssetRecord[]> {
    const db = await getDB();

    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const index = store.index('projectId');
        const request = index.getAll(projectId);

        request.onsuccess = () => {
            const results = request.result as LocalAssetRecord[];
            results.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
            resolve(results);
        };
        request.onerror = () => reject(new Error('Failed to get project assets'));
    });
}

/**
 * Clear all assets (use with caution)
 */
export async function clearAllAssets(): Promise<void> {
    const db = await getDB();

    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const request = store.clear();

        request.onsuccess = () => resolve();
        request.onerror = () => reject(new Error('Failed to clear assets'));
    });
}

/**
 * Get total count of assets
 */
export async function getAssetCount(): Promise<number> {
    const db = await getDB();

    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const request = store.count();

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(new Error('Failed to count assets'));
    });
}
